import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auditLogsApi } from '../../services/auditLogs';

function AuditLogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [auditLog, setAuditLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAuditLog();
  }, [id]);

  const fetchAuditLog = async () => {
    try {
      setLoading(true);
      const data = await auditLogsApi.getById(id);
      setAuditLog(data);
    } catch (err) {
      setError('Failed to fetch audit log details');
    } finally {
      setLoading(false);
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      create: 'Create',
      update: 'Update',
      delete: 'Delete',
      assign: 'Assign',
      return: 'Return',
      create_and_assign: 'Create & Assign'
    };
    return labels[action] || action;
  };

  const getEntityTypeLabel = (entityType) => {
    const labels = {
      asset: 'Asset',
      employee: 'Employee',
      client: 'Client',
      proposal: 'Proposal',
      purchase_order: 'Purchase Order'
    };
    return labels[entityType] || entityType;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const renderDiff = () => {
    const oldValues = auditLog.old_values ? JSON.parse(auditLog.old_values) : {};
    const newValues = auditLog.new_values ? JSON.parse(auditLog.new_values) : {};
    
    const allKeys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);
    const keysArray = Array.from(allKeys).sort();
    
    if (keysArray.length === 0) {
      return <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>No changes to display</div>;
    }

    return (
      <table className="data-table" style={{ fontSize: '14px' }}>
        <thead>
          <tr>
            <th style={{ width: '30%', color: '#333', backgroundColor: '#f5f5f5' }}>Field</th>
            <th style={{ width: '35%', color: '#333', backgroundColor: '#f5f5f5' }}>Old Value</th>
            <th style={{ width: '35%', color: '#333', backgroundColor: '#f5f5f5' }}>New Value</th>
          </tr>
        </thead>
        <tbody>
          {keysArray.map(key => {
            const oldValue = oldValues[key];
            const newValue = newValues[key];
            const isChanged = oldValue !== newValue;
            
            return (
              <tr key={key} style={{ backgroundColor: isChanged ? '#fff8e1' : 'transparent' }}>
                <td style={{ fontWeight: '500', padding: '12px', color: isChanged ? '#000000' : ''}}>{key}</td>
                <td style={{ 
                  color: isChanged && oldValue !== null ? '#d32f2f' : '',
                  backgroundColor: isChanged && oldValue !== null ? '#ffebee' : 'transparent',
                  padding: '12px'
                }}>
                  {formatValue(oldValue)}
                </td>
                <td style={{ 
                  color: isChanged && newValue !== null ? '#388e3c' : '',
                  backgroundColor: isChanged && newValue !== null ? '#e8f5e9' : 'transparent',
                  padding: '12px'
                }}>
                  {formatValue(newValue)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  if (loading) return <div className="loading">Loading audit log details...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!auditLog) return <div className="alert alert-error">Audit log not found</div>;

  return (
    <div>
      <div className="page-header">
        <h2>Audit Log Details</h2>
        <button className="btn btn-secondary" onClick={() => navigate('/audit-logs')}>
          Back to List
        </button>
      </div>

      <div className="card">
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>Date</label>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                {new Date(auditLog.created_at).toLocaleString()}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>Entity Type</label>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                {getEntityTypeLabel(auditLog.entity_type)}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>Entity ID</label>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                {auditLog.entity_id}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>Action</label>
              <div>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  backgroundColor: auditLog.action === 'delete' ? '#ffebee' : 
                                 auditLog.action === 'create' ? '#e8f5e9' :
                                 auditLog.action === 'update' ? '#e3f2fd' : '#fff3e0',
                  color: auditLog.action === 'delete' ? '#c62828' : 
                         auditLog.action === 'create' ? '#2e7d32' :
                         auditLog.action === 'update' ? '#1565c0' : '#e65100'
                }}>
                  {getActionLabel(auditLog.action)}
                </span>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>Changed By</label>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                {auditLog.changed_by_name || auditLog.changed_by || 'System'}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>Role</label>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                {auditLog.changed_by_role || '-'}
              </div>
            </div>
          </div>
        </div>

        <h3 style={{ marginBottom: '16px' }}>Changes</h3>
        <div className="table-container">
          {renderDiff()}
        </div>
      </div>
    </div>
  );
}

export default AuditLogDetail;
